import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';

// Tiptap Imports
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Code from '@tiptap/extension-code';
import CodeBlock from '@tiptap/extension-code-block';

import AppBreadCrumb from '../../../components/common/AppBreadCrumb';
import AppToaster from '../../../components/AppToaster';
import { createDocument, fetchDocument, updateDocument, getDocumentsForDropdown, uploadDocumentImage } from '../../../api/DocumentsAPI';
import { getTags } from '../../../api/TagsAPI';
import { handleApiError } from '../../../api/risapi';
import './Tiptap.css';

const MenuBar = ({ editor }) => {
    const addImage = useCallback(() => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (file) {
                uploadDocumentImage(file, (response) => {
                    if (response.url) {
                        editor.chain().focus().setImage({ src: response.url }).run();
                    } else {
                        console.error("Image upload failed");
                    }
                });
            }
        };
        input.click();
    }, [editor]);

    if (!editor) {
        return null;
    }

    return (
        <div className="tiptap-menu-bar">
            <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'is-active' : ''}>Bold</button>
            <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'is-active' : ''}>Italic</button>
            <button type="button" onClick={() => editor.chain().focus().toggleStrike().run()} className={editor.isActive('strike') ? 'is-active' : ''}>Strike</button>
            <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}>H1</button>
            <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}>H2</button>
            <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive('bulletList') ? 'is-active' : ''}>List</button>
            <button type="button" onClick={addImage}>Image</button>
            <button type="button" onClick={() => editor.chain().focus().toggleCode().run()} className={editor.isActive('code') ? 'is-active' : ''}>Inline Code</button>
            <button type="button" onClick={() => editor.chain().focus().toggleCodeBlock().run()} className={editor.isActive('codeBlock') ? 'is-active' : ''}>Code Block</button>
        </div>
    );
};


const DocumentsForm = ({ formType, currentDocumentId }) => {
    const navigate = useNavigate();
    const [document, setDocument] = useState({ title: '', content: '', status: 'draft', parent_id: null, tag_ids: [] });
    const [errors, setErrors] = useState({});
    const [showToaster, setShowToaster] = useState(false);
    const [toaster, setToaster] = useState(null); // <-- FIX: Initialize to null
    const [parentDocuments, setParentDocuments] = useState([]);
    const [tags, setTags] = useState([]);

    useEffect(() => {
        getDocumentsForDropdown((response) => {
            if (response.documents) {
                setParentDocuments(response.documents.map(doc => ({ value: doc.id, label: doc.title })));
            }
        });
        getTags({ paginate: false }, (response) => {
            if (response.tags) {
                setTags(response.tags.map(tag => ({ value: tag.id, label: tag.name })));
            }
        });

        if (formType === 'edit' && currentDocumentId) {
            fetchDocument(currentDocumentId, (response) => {
                if (response.document) {
                    const doc = response.document;
                    setDocument({
                        ...doc,
                        parent_id: doc.parent ? { value: doc.parent.id, label: doc.parent.title } : null,
                        tag_ids: doc.tags ? doc.tags.map(tag => ({ value: tag.id, label: tag.name })) : []
                    });
                }
            });
        }
    }, [formType, currentDocumentId]);

    const editor = useEditor({
        extensions: [
            StarterKit,
            Image,
            Link,
            Code,
            CodeBlock,
        ],
        content: document.content,
        onUpdate: ({ editor }) => {
            setDocument(prevDoc => ({ ...prevDoc, content: editor.getHTML() }));
        },
    });

    useEffect(() => {
        if (editor && document.content && document.content !== editor.getHTML()) {
            editor.commands.setContent(document.content);
        }
    }, [document.content, editor]);


    const getTitle = () => formType === 'create' ? 'Create New Document' : 'Edit Document';

    const handleSave = (e) => {
        e.preventDefault();
        setErrors({});

        const submissionData = {
            ...document,
            parent_id: document.parent_id ? document.parent_id.value : null,
            tag_ids: document.tag_ids.map(tag => tag.value),
        };

        const apiCall = formType === 'create' ? createDocument : updateDocument;

        apiCall(submissionData, (response) => {
            if (response.document) {
                navigate('/docs/documents', { state: { message: `Document ${formType === 'create' ? 'created' : 'updated'} successfully!` } });
            } else if (response.error) {
                const errorData = response.error.response?.data?.errors;
                if (errorData) {
                    setErrors(errorData);
                } else {
                    setToaster({ type: 'error', heading: 'Error', message: 'An unexpected error occurred.' });
                    setShowToaster(true);
                }
            }
        });
    };

    return (
        <>
            {showToaster && <AppToaster showToaster={setShowToaster} toaster={toaster} />} 
            <main>
                <div className="container-fluid">
                    <AppBreadCrumb title={getTitle()}>
                        <li><a className="f-s-14 f-w-500" href="#">Docs Master</a></li>
                        <li><a className="f-s-14 f-w-500" href="/docs/documents">Documents</a></li>
                        <li className="active"><a className="f-s-14 f-w-500" href="#">{formType === 'create' ? 'Create' : 'Edit'}</a></li>
                    </AppBreadCrumb>

                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">
                                    <h5>{getTitle()} {formType === 'edit' ? `: ${document.title}` : ''}</h5>
                                </div>
                                <div className="card-body">
                                    <form className="app-form" onSubmit={handleSave}>
                                        <div className="row">
                                            <div className="col-md-12">
                                                <div className="mb-3">
                                                    <label className="form-label">Title *</label>
                                                    <input
                                                        className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                                                        placeholder="Enter Document Title"
                                                        value={document.title || ''}
                                                        onChange={(e) => setDocument({ ...document, title: e.target.value })}
                                                        required
                                                    />
                                                    {errors.title && <div className="invalid-feedback">{errors.title[0]}</div>}
                                                </div>
                                            </div>

                                            <div className="col-md-12 mb-3">
                                                <label className="form-label">Content *</label>
                                                <div className={`tiptap-editor-wrapper ${errors.content ? 'is-invalid' : ''}`}>
                                                    <MenuBar editor={editor} />
                                                    <EditorContent editor={editor} />
                                                </div>
                                                 {errors.content && <div className="invalid-feedback d-block">{errors.content[0]}</div>}
                                            </div>

                                            <div className="col-md-6 mb-3">
                                                 <label className="form-label">Parent Document</label>
                                                 <Select
                                                    options={parentDocuments}
                                                    value={document.parent_id}
                                                    onChange={(selected) => setDocument({...document, parent_id: selected})}
                                                    isClearable
                                                 />
                                            </div>

                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Status *</label>
                                                <select className="form-select" value={document.status} onChange={(e) => setDocument({...document, status: e.target.value})}>
                                                    <option value="draft">Draft</option>
                                                    <option value="published">Published</option>
                                                    <option value="archived">Archived</option>
                                                </select>
                                            </div>
                                            
                                            <div className="col-md-12 mb-3">
                                                 <label className="form-label">Tags</label>
                                                 <Select
                                                    options={tags}
                                                    value={document.tag_ids}
                                                    onChange={(selected) => setDocument({...document, tag_ids: selected})}
                                                    isMulti
                                                 />
                                            </div>

                                            <div className="col-12">
                                                <div className="text-end">
                                                    <button className="btn btn-primary me-2" type="submit">
                                                        {formType === 'create' ? 'Save Document' : 'Update Document'}
                                                    </button>
                                                    <button className="btn btn-danger" type="button" onClick={() => navigate('/docs/documents')}>
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default DocumentsForm;

